import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './SchoolDashboard.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const SupportSchoolDashboard = () => {
  const navigate = useNavigate();
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    if (!token) {
      navigate('/business/signup/support', { replace: true });
      return;
    }

    fetch(`${API_BASE_URL}/api/schools/me`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((res) => {
        if (res.status === 401) {
          navigate('/business/signup/support', { replace: true });
          return null;
        }
        return res.json();
      })
      .then((data) => {
        if (data && data.success && data.school) {
          if (data.school.schoolType !== 'support') {
            navigate('/business/dashboard/accredited', { replace: true });
            return;
          }
          setSchool(data.school);
        }
      })
      .catch(() => navigate('/business/signup/support', { replace: true }))
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('token');
    navigate('/business');
  };

  const sections = [
    { id: 'overview', label: 'Overview', icon: 'dashboard' },
    { id: 'students', label: 'Students', icon: 'people' },
    { id: 'attendance', label: 'Attendance', icon: 'event_available' },
    { id: 'payments', label: 'Payments & Finance', icon: 'payments' },
    { id: 'performance', label: 'Performance', icon: 'trending_up' }
  ];

  if (loading) {
    return (
      <div className="school-dashboard">
        <div className="school-dashboard-loading">
          <span className="material-icons spin">refresh</span>
          <p>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (!school) return null;

  return (
    <div className="school-dashboard support-dashboard">
      <aside className="school-dashboard-sidebar">
        <div className="school-dashboard-sidebar-header">
          <span className="material-icons">school</span>
          <span>Syllera</span>
        </div>
        <div className="school-dashboard-school-name">{school.name}</div>
        <p className="school-dashboard-school-type">Support School</p>
        <nav className="school-dashboard-nav">
          {sections.map((s) => (
            <button
              key={s.id}
              type="button"
              className={`school-dashboard-nav-btn ${activeSection === s.id ? 'active' : ''}`}
              onClick={() => setActiveSection(s.id)}
            >
              <span className="material-icons">{s.icon}</span>
              {s.label}
            </button>
          ))}
        </nav>
        <div className="school-dashboard-sidebar-footer">
          <Link to="/business" className="school-dashboard-link">Business home</Link>
          <button type="button" className="school-dashboard-logout" onClick={handleLogout}>
            <span className="material-icons">logout</span> Log out
          </button>
        </div>
      </aside>

      <main className="school-dashboard-main">
        <header className="school-dashboard-header">
          <h1>Support School Dashboard</h1>
          <p>{school.name} · {school.city}</p>
        </header>

        {activeSection === 'overview' && (
          <div className="school-dashboard-section">
            <h2>Overview</h2>
            <div className="school-dashboard-cards">
              <div className="school-dashboard-card">
                <span className="material-icons">group</span>
                <div className="school-dashboard-card-value">0</div>
                <div className="school-dashboard-card-label">Total Students</div>
              </div>
              <div className="school-dashboard-card">
                <span className="material-icons">check_circle</span>
                <div className="school-dashboard-card-value">0</div>
                <div className="school-dashboard-card-label">Present Today</div>
              </div>
              <div className="school-dashboard-card">
                <span className="material-icons">cancel</span>
                <div className="school-dashboard-card-value">0</div>
                <div className="school-dashboard-card-label">Absent Today</div>
              </div>
              <div className="school-dashboard-card">
                <span className="material-icons">schedule</span>
                <div className="school-dashboard-card-value">0</div>
                <div className="school-dashboard-card-label">Late Students</div>
              </div>
              <div className="school-dashboard-card">
                <span className="material-icons">notifications_active</span>
                <div className="school-dashboard-card-value">0</div>
                <div className="school-dashboard-card-label">Parents Notified</div>
              </div>
              <div className="school-dashboard-card">
                <span className="material-icons">attach_money</span>
                <div className="school-dashboard-card-value">—</div>
                <div className="school-dashboard-card-label">Monthly Revenue</div>
              </div>
            </div>
            <p className="school-dashboard-placeholder">Student and revenue data will appear here once you add students and record payments.</p>
          </div>
        )}

        {activeSection === 'students' && (
          <div className="school-dashboard-section">
            <h2>Students</h2>
            <p className="school-dashboard-placeholder">Add students, assign to groups (BAC / BEM / 2AS), view attendance history, payment status, behavior notes, and parent contact. This section will be implemented next.</p>
          </div>
        )}

        {activeSection === 'attendance' && (
          <div className="school-dashboard-section">
            <h2>Attendance</h2>
            <p className="school-dashboard-placeholder">Mark present/absent, automatic parent SMS notifications, monthly reports, and chronic absentees. This section will be implemented next.</p>
          </div>
        )}

        {activeSection === 'payments' && (
          <div className="school-dashboard-section">
            <h2>Payments & Finance</h2>
            <p className="school-dashboard-placeholder">Student monthly fees, paid/unpaid status, partial payments, revenue per month, and export financial reports. This section will be implemented next.</p>
          </div>
        )}

        {activeSection === 'performance' && (
          <div className="school-dashboard-section">
            <h2>Performance</h2>
            <p className="school-dashboard-placeholder">Exam scores, homework completion, ranking inside group, and at-risk students. This section will be implemented next.</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default SupportSchoolDashboard;
